/* ============================================================================
   Freebie sync for Streamline (Shopify)
   v3.5.3 — safety shim compat + resilient globals + strict coupon gate (multi-code)
            + 66/99 single-gift local logic + banner fix (missing applied flag)
            + circuit breaker + watchdog + compact banner + returns cart
   Endpoint: https://spyandsurvival.com/freebie/index.php
   ========================================================================== */

(function(){
  'use strict';

  /* ===== Duplicate-load guard (silent) ===== */
  if (window.__FREEBIE_WIRED__) { console.warn('freebie: already wired; skipping re-init'); return; }
  window.__FREEBIE_WIRED__ = true;

  /* ===== CONFIG (edit only if SKUs change) ===== */
  const EXTERNAL_ENDPOINT   = 'https://spyandsurvival.com/freebie/index.php';
  const SHARED_TOKEN        = 'kj82h39sd9239823sdf';
  const THRESHOLD_CENTS     = 10000; // $100
  const REQUIRED_CODES      = ['FREEDOM13', 'FREEDOM77', 'FREEDOM99', 'FREEDOM66'];
  const FREEBIE_VARIANT_ID  = 47002578419947; // Spy Blade XL (Freedom) was 46610049695979
  const TOMAHAWK_VARIANT_ID = 46765946962155; // Ultralight Tactical Tomahawk
  
  /* Expose a few globals for console QA */
  window.FREEBIE_VARIANT_ID  = FREEBIE_VARIANT_ID;
  window.TOMAHAWK_VARIANT_ID = TOMAHAWK_VARIANT_ID;
  window.THRESHOLD_CENTS     = THRESHOLD_CENTS;

  /* Theme (Streamline) selectors */
  const CART_DRAWER_SELECTOR = '#CartDrawer';
  const DRAWER_CLOSE_BTN     = '#CartDrawer .js-drawer-close';

  /* ===== Internal state ===== */
  let FREEBIE_SYNCING = false;
  let LAST_INSTR_SIG  = '';
  let LAST_CHECK_AT   = 0;
  let LAST_NONEMPTY_AT = 0;
  let LAST_ACTION_AT  = 0;
  const NONEMPTY_GRACE_MS   = 5000;
  const ACTION_COOLDOWN_MS  = 1000;
  let SUPPRESS_ENSURE_UNTIL = 0;
  let SUPPRESS_ADDED_UNTIL  = 0;
  let LAST_CART_FP = { subtotal: 0, itemCount: 0 };

  // Banner/UI state
  let APPLYING_CODE    = false;
  let GIFT_ADDING      = false;
  let PREV_GIFT_QTY    = 0;
  let JUST_ADDED_UNTIL = 0;

  const sleep = (ms) => new Promise(r => setTimeout(r, ms));
  const $ = window.jQuery;

  /* ===== Styles (once) ===== */
  (function injectFreebieStylesOnce(){
    if (document.getElementById('freebie-banner-styles')) return;
    const style = document.createElement('style');
    style.id = 'freebie-banner-styles';
    style.textContent = `
      #freebie-message{display:inline-flex;align-items:center;min-height:24px;font-size:12px;line-height:1.2;color:#0f172a}
      .freebie-badge{display:inline-flex;align-items:center;gap:6px;padding:4px 8px;border:1px solid;border-radius:9999px;white-space:nowrap}
      .freebie-badge--info{background:#f8fafc;border-color:#e2e8f0;color:#334155}
      .freebie-badge--warn{background:#fff7ed;border-color:#fed7aa;color:#9a3412}
      .freebie-badge--ok{background:#f0fdf4;border-color:#bbf7d0;color:#166534}
      .freebie-badge--load{background:#eef2ff;border-color:#c7d2fe;color:#3730a3}
      .freebie-icon{width:12px;height:12px;display:inline-block}
      .freebie-dot{width:6px;height:6px;border-radius:50%;background:currentColor;opacity:.6;display:inline-block}
      .freebie-spinner{width:12px;height:12px;border:2px solid currentColor;border-top-color:transparent;border-radius:50%;display:inline-block;animation:freebie-spin .8s linear infinite}
      @keyframes freebie-spin{to{transform:rotate(360deg)}}
    `;
    document.head.appendChild(style);
  })();

  /* ===== Cart helpers ===== */
  async function getCart() {
    const r = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
    return r.json();
  }

  function appliedCodes(cart){
    const raw = (cart && Array.isArray(cart.discount_codes)) ? cart.discount_codes : [];
    return raw
      .filter(e => (typeof e === 'object' && 'applied' in e) ? !!e.applied : true)
      .map(e => (typeof e === 'object' ? String(e && e.code || '') : String(e || '')))
      .map(s => s.toUpperCase())
      .filter(Boolean);
  }

  function hasRequiredCode(cart){
    const raw = (cart && Array.isArray(cart.discount_codes)) ? cart.discount_codes : [];
    if (!raw.length) return false;
    const allow = new Set(REQUIRED_CODES.map(s => String(s).toUpperCase()));
    return raw.some(entry => {
      const code = (typeof entry === 'object') ? String((entry && entry.code) || '') : String(entry || '');
      const applied = (typeof entry === 'object' && 'applied' in entry) ? !!entry.applied : true;
      return applied && allow.has(code.toUpperCase());
    });
  }

  function countAnyFreebieQty(cart) {
    return (cart.items || []).reduce((s, line) => {
      const id = Number(line?.id);
      return s + ((id === Number(FREEBIE_VARIANT_ID) || id === Number(TOMAHAWK_VARIANT_ID)) ? Number(line.quantity || 0) : 0);
    }, 0);
  }

  function countAnyQtyByVariant(cart, variantId){
    return (cart.items || []).reduce((s, line) => s + (Number(line?.id) === Number(variantId) ? Number(line.quantity || 0) : 0), 0);
  }

  function countTaggedQty(cart, variantId, tag){
    tag = String(tag||'').toLowerCase();
    return (cart.items || []).reduce((sum, line) => {
      const isId = Number(line?.id) === Number(variantId);
      const ltag = String(line?.properties?._gift || '').toLowerCase();
      return sum + (isId && ltag === tag ? Number(line.quantity || 0) : 0);
    }, 0);
  }

  async function removeAnyFreebieVariant(maybeCart) {
    let cart = maybeCart || await getCart();
    const lines = (cart.items || []).filter(i => {
      const id = Number(i?.id);
      return id === Number(FREEBIE_VARIANT_ID) || id === Number(TOMAHAWK_VARIANT_ID);
    });
    for (const line of lines) {
      await fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: line.key, quantity: 0 }) });
      await sleep(60);
    }
    LAST_ACTION_AT = Date.now();
    await emitCartUpdate();
  }

  function isEligible(cart) {
    const subtotal = Number(cart?.items_subtotal_price || 0);
    return { hasCode: hasRequiredCode(cart), meetsSpend: subtotal >= THRESHOLD_CENTS };
  }

  function rememberCartState(cart) {
    if (!cart) return;
    LAST_CART_FP = { subtotal: Number(cart.items_subtotal_price || 0), itemCount: Number(cart.item_count || 0) };
    if (cart.item_count > 0) LAST_NONEMPTY_AT = Date.now();
  }

  async function eligibilityStable(delayMs = 400) {
    const c1 = await getCart();
    const e1 = isEligible(c1);
    const fp1 = { sub: Number(c1.items_subtotal_price || 0), count: Number(c1.item_count || 0) };
    await sleep(delayMs);
    const c2 = await getCart();
    const e2 = isEligible(c2);
    const fp2 = { sub: Number(c2.items_subtotal_price || 0), count: Number(c2.item_count || 0) };
    const sameTotals = (fp1.sub === fp2.sub) && (fp1.count === fp2.count);
    const eligibleBoth = (e1.hasCode && e1.meetsSpend) && (e2.hasCode && e2.meetsSpend);
    return sameTotals && eligibleBoth;
  }

  async function postToService(cart) {
    const r = await fetch(EXTERNAL_ENDPOINT, {
      method: 'POST', mode: 'cors', cache: 'no-store',
      headers: { 'Content-Type': 'application/json', 'X-Freebie-Token': SHARED_TOKEN },
      body: JSON.stringify(cart)
    });
    return r.json();
  }

  /* ===== Theme/UI helpers ===== */
  function closeCartDrawer() {
    const btn = document.querySelector(DRAWER_CLOSE_BTN);
    if (btn) btn.click();
    else {
      document.body.classList.remove('js-drawer-open', 'js-drawer-closing');
      const drawer = document.querySelector(CART_DRAWER_SELECTOR);
      if (drawer) drawer.classList.remove('drawer--is-open');
    }
  }

  async function emitCartUpdate() {
    try {
      const cart = await getCart();
      rememberCartState(cart);
      if ($ && $('body').trigger) $('body').trigger('updateCart', cart);
      await updateFreebieBanner(cart);
      await maybeCloseDrawerIfEmpty(cart);
    } catch (_) {}
  }

  /* ===== Banner renderers ===== */
  function setBannerDefault() {
    const el = document.getElementById('freebie-message');
    if (!el) return;
    el.innerHTML = `<span class="freebie-badge freebie-badge--info"></span>`;
  }
  function setBannerBusy(text) {
    const el = document.getElementById('freebie-message');
    if (!el) return;
    el.innerHTML = `
      <span class="freebie-badge freebie-badge--load">
        <span class="freebie-spinner" aria-hidden="true"></span>
        <span>${text}</span>
      </span>`;
  }

  /* ===== Banner controller ===== */
  async function updateFreebieBanner(maybeCart) {
    const el = document.getElementById('freebie-message');
    if (!el) return;

    if (APPLYING_CODE) { setBannerBusy('Applying code…'); return; }

    let cart = maybeCart;
    try { if (!cart) cart = await getCart(); } catch(_) {}
    if (!cart) { setBannerDefault(); return; }

    const codes = new Set(appliedCodes(cart));
    const useTomahawk = codes.has('FREEDOM66');
    const useOne      = codes.has('FREEDOM99') || useTomahawk; // single-gift local path
    const targetQty   = useOne ? 1 : 2;
    const label       = useTomahawk ? 'Ultralight Tactical Tomahawk' : (useOne ? 'Spy Blade XL (Freedom)' : 'Spy Blade XL (Freedom)');

    const neededVariant = useTomahawk ? TOMAHAWK_VARIANT_ID : FREEBIE_VARIANT_ID;
    const presentQtyForVariant = countAnyQtyByVariant(cart, neededVariant);

    const giftPresent = useOne ? (presentQtyForVariant >= 1) : (countAnyFreebieQty(cart) >= 2);

    if (GIFT_ADDING) {
      el.innerHTML = `
        <span class="freebie-badge freebie-badge--load">
          <span class="freebie-spinner" aria-hidden="true"></span>
          <span>Adding ${targetQty} Free ${label}…</span>
        </span>`;
      return;
    }

    if (giftPresent) {
      if (Date.now() < SUPPRESS_ADDED_UNTIL) {
        setBannerDefault();
      } else {
        el.innerHTML = `
          <span class="freebie-badge freebie-badge--ok">
            <svg class="freebie-icon" viewBox="0 0 16 16" aria-hidden="true">
              <path fill="currentColor" d="M6.2 11.3 2.9 8l1.3-1.3 2 2 5-5L12.7 5l-6.5 6.3z"/>
            </svg>
            <span>${targetQty} Free ${label} added</span>
          </span>`;
      }
      PREV_GIFT_QTY = useOne ? Math.max(1, presentQtyForVariant) : countAnyFreebieQty(cart);
      return;
    }

    PREV_GIFT_QTY = useOne ? presentQtyForVariant : countAnyFreebieQty(cart);

    const { hasCode, meetsSpend } = isEligible(cart);
    if (hasCode && !meetsSpend) {
      const diff = (THRESHOLD_CENTS - (cart.items_subtotal_price || 0)) / 100;
      el.innerHTML = `
        <span class="freebie-badge freebie-badge--warn">
          <span class="freebie-dot" aria-hidden="true"></span>
          <span>Add $${diff.toFixed(2)} more to unlock <strong>${targetQty} ${label}</strong>.</span>
        </span>`;
      return;
    }

    setBannerDefault();
  }
  window.updateFreebieBanner = updateFreebieBanner;

  /* ===== Gift mutations ===== */
  async function ensureGift(instr) {
    if (Date.now() < SUPPRESS_ENSURE_UNTIL) return;
    const c0 = await getCart();
    const { hasCode, meetsSpend } = isEligible(c0);
    if (!(hasCode && meetsSpend)) return;

    const stable = await eligibilityStable(400);
    if (!stable) return;

    const cart = await getCart();
    const alreadyTagged = countTaggedQty(cart, instr.variant_id, (instr.properties?._gift)||'kukri');
    const target  = Number(instr.quantity || 0);
    if (alreadyTagged >= target) return;

    const delta = target - alreadyTagged;
    GIFT_ADDING = true;
    try {
      await fetch('/cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: instr.variant_id, quantity: delta, properties: instr.properties || { _gift: 'kukri' } })
      });
      JUST_ADDED_UNTIL = Date.now() + 5000;
      LAST_ACTION_AT   = Date.now();
      await emitCartUpdate();
    } finally {
      GIFT_ADDING = false;
      await updateFreebieBanner();
    }
  }

  async function ensureTaggedGift(variantId, tag, targetQty){
    const c0 = await getCart();
    const have = countTaggedQty(c0, variantId, tag);
    if (have >= targetQty) return;

    const delta = targetQty - have;
    GIFT_ADDING = true;
    await updateFreebieBanner(c0);
    try {
      await fetch('/cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: delta, properties: { _gift: String(tag||'').toLowerCase() } })
      });
      JUST_ADDED_UNTIL = Date.now() + 5000;
      LAST_ACTION_AT   = Date.now();
      await emitCartUpdate();
    } finally {
      GIFT_ADDING = false;
      await updateFreebieBanner();
    }
  }

  async function removeTaggedGift(variantId, tag){
    const c0 = await getCart();
    const lines = (c0.items || []).filter(i => {
      const isId = Number(i?.id) === Number(variantId);
      const ltag = String(i?.properties?._gift || '').toLowerCase();
      return isId && ltag === String(tag||'').toLowerCase();
    });
    for (const line of lines) {
      await fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: line.key, quantity: 0 }) });
      await sleep(60);
    }
    if (lines.length) { LAST_ACTION_AT = Date.now(); await emitCartUpdate(); }
  }

  async function removeGiftByVariant(variantId) {
    const cart = await getCart();
    const { hasCode, meetsSpend } = isEligible(cart);
    const stillEligible = hasCode && meetsSpend;
    if (stillEligible) return; // do not remove while eligible

    const lines = (cart.items || []).filter(i => Number(i?.id) === Number(variantId));
    for (const line of lines) {
      await fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: line.key, quantity: 0 }) });
      await sleep(60);
    }
    LAST_ACTION_AT = Date.now();
    await emitCartUpdate();
  }

  async function applyInstruction(instr) {
    if (!instr || !instr.action) return;
    if (Date.now() - LAST_ACTION_AT < ACTION_COOLDOWN_MS) return;

    const sig = JSON.stringify(instr);
    if (sig === LAST_INSTR_SIG && FREEBIE_SYNCING) return;

    FREEBIE_SYNCING = true; LAST_INSTR_SIG = sig;
    try {
      if (instr.action === 'ensure') {
        const cartNow = await getCart();
        const { hasCode, meetsSpend } = isEligible(cartNow);
        if (!(hasCode && meetsSpend)) { if (countAnyFreebieQty(cartNow) > 0) await removeAnyFreebieVariant(cartNow); return; }
        await ensureGift(instr);
      } else if (instr.action === 'remove') {
        await removeGiftByVariant(instr.variant_id);
      }
    } finally {
      FREEBIE_SYNCING = false;
    }
  }

  /* ===== Orchestrator ===== */
  async function triggerFreebieCheck() {
    const now = Date.now();
    if (FREEBIE_SYNCING || (now - LAST_CHECK_AT) < 500) return;
    LAST_CHECK_AT = now;

    try {
      const cart = await getCart();
      const { hasCode, meetsSpend } = isEligible(cart);

      // Ineligible → remove freebies + refresh UI
      if (!(hasCode && meetsSpend)) {
        if (countAnyFreebieQty(cart) > 0) await removeAnyFreebieVariant(cart);
        await updateFreebieBanner(cart);
        await maybeCloseDrawerIfEmpty(cart);
        return;
      }

      // Local handling for 66/99; service for 13/77
      const codes = new Set(appliedCodes(cart));
      if (codes.has('FREEDOM66')) {
        await removeTaggedGift(FREEBIE_VARIANT_ID, 'kukri');
        await ensureTaggedGift(TOMAHAWK_VARIANT_ID, 'tomahawk', 1);
      } else if (codes.has('FREEDOM99')) {
        await removeTaggedGift(TOMAHAWK_VARIANT_ID, 'tomahawk');
        await ensureTaggedGift(FREEBIE_VARIANT_ID, 'kukri', 1);
      } else {
        const instr = await postToService(cart);
        await applyInstruction(instr);
      }

      if (document && document.dispatchEvent) {
        document.dispatchEvent(new CustomEvent('cart:gift-synced'));
      }
    } catch (_) {}
  }
  window.triggerFreebieCheck = triggerFreebieCheck;

  /* ===== Drawer auto-close when empty (with grace) ===== */
  async function maybeCloseDrawerIfEmpty(maybeCart, opts = {}) {
    const delay = opts.delay || 0;
    if (Date.now() - LAST_NONEMPTY_AT < NONEMPTY_GRACE_MS) return;
    if (delay) await sleep(delay);

    let cart = maybeCart;
    try { if (!cart) cart = await getCart(); } catch(_){ }

    if (cart && cart.item_count > 0) { rememberCartState(cart); return; }
    setBannerDefault();
    closeCartDrawer();
  }

  /* ===== Discount helper (returns cart) ===== */
  async function applyDiscountAjax(code) {
    try {
      APPLYING_CODE = true;
      const trimmed = (code || '').trim();
      setBannerBusy(trimmed ? 'Applying code…' : 'Removing code…');

      const res = await fetch('/cart/update.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'same-origin',
        body: JSON.stringify({ discount: trimmed })
      });

      const cart = await res.json();
      rememberCartState(cart);

      if ($ && $('body').trigger) $('body').trigger('updateCart', cart);

      await triggerFreebieCheck();
      await updateFreebieBanner(cart);
      await maybeCloseDrawerIfEmpty(cart);
      return cart; // ← important: no more undefined
    } catch (e) {
      console.error('applyDiscountAjax error:', e);
      return null;
    } finally {
      APPLYING_CODE = false;
      await updateFreebieBanner();
    }
  }
  window.applyDiscountAjax = applyDiscountAjax;

  /* ===== Watchdog ===== */
  function startFreebieWatchdog(windowMs = 2500, tickMs = 250) {
    const stopAt = Date.now() + windowMs;
    const timer = setInterval(async () => {
      if (Date.now() > stopAt) { clearInterval(timer); return; }
      try {
        const c = await getCart();
        PREV_GIFT_QTY = countAnyFreebieQty(c);
        await updateFreebieBanner(c);

        const { hasCode, meetsSpend } = isEligible(c);
        if (!(hasCode && meetsSpend) && PREV_GIFT_QTY > 0) {
          await removeAnyFreebieVariant(c);
          const c2 = await getCart();
          PREV_GIFT_QTY = countAnyFreebieQty(c2);
          await updateFreebieBanner(c2);
        }
      } catch(_) {}
    }, tickMs);
  }

  /* ===== Boot ===== */
  document.addEventListener('DOMContentLoaded', async () => {
    try {
      // Ensure a banner host exists early
      if (!document.getElementById('freebie-message')) {
        const host = document.querySelector('.Cart__recap, .cart__footer, #CartDrawer .drawer__inner, body');
        const el = document.createElement('div'); el.id = 'freebie-message'; host?.prepend(el);
      }
      const cart = await getCart();
      PREV_GIFT_QTY = countAnyFreebieQty(cart);
      rememberCartState(cart);
      await updateFreebieBanner(cart);
    } catch(_) {}
    await triggerFreebieCheck();
  });

  /* ===== Theme integration ===== */
  if ($ && $('body').on) {
    $('body').on('updateCart.freebie', async function (_evt, cart) {
      try {
        const nextSubtotal = Number(cart.items_subtotal_price || 0);
        const nextCount    = Number(cart.item_count || 0);
        if (nextSubtotal < LAST_CART_FP.subtotal || nextCount < LAST_CART_FP.itemCount) {
          const now = Date.now();
          SUPPRESS_ENSURE_UNTIL = now + 2200;
          SUPPRESS_ADDED_UNTIL  = now + 1200;
        }
      } catch(_) {}
      PREV_GIFT_QTY = countAnyFreebieQty(cart);
      rememberCartState(cart);
      startFreebieWatchdog();
      await updateFreebieBanner(cart);
      await triggerFreebieCheck();
      await maybeCloseDrawerIfEmpty(cart);
    });
  }

  if ($ && $('body').on) {
    $('body').on('added.ajaxProduct', function () { LAST_NONEMPTY_AT = Date.now(); startFreebieWatchdog(); });
  }
  document.addEventListener('added:ajaxProduct', function () { LAST_NONEMPTY_AT = Date.now(); startFreebieWatchdog(); });

  document.addEventListener('click', (e) => {
    if (e.target && (e.target.matches('[data-cart-item-delete]') || e.target.closest('[data-cart-item-delete]'))) {
      const now = Date.now();
      SUPPRESS_ENSURE_UNTIL = now + 2200;
      SUPPRESS_ADDED_UNTIL  = now + 1500;
    }
  });
  document.addEventListener('change', (e) => {
    const el = e.target;
    if (el && el.matches('[data-cart-item-input-quantity]') && Number(el.value || 0) === 0) {
      const now = Date.now();
      SUPPRESS_ENSURE_UNTIL = now + 2200;
      SUPPRESS_ADDED_UNTIL  = now + 1500;
    }
  });

  if ($ && $('body').on) {
    $('body').on('drawerOpen.CartDrawer', async function(){ await maybeCloseDrawerIfEmpty(null, { delay: 900 }); });
  }

  document.addEventListener('cart:gift-synced', async () => {
    try { const c = await getCart(); PREV_GIFT_QTY = countAnyFreebieQty(c); await updateFreebieBanner(c); } catch(_) {}
  });

  /* ===== FINAL override: crystal-clear 66/99 banner rendering ===== */
  (function(){
    const BLADE_ID    = (window.FREEBIE_VARIANT_ID  != null ? window.FREEBIE_VARIANT_ID  : FREEBIE_VARIANT_ID);
    const TOMAHAWK_ID = (window.TOMAHAWK_VARIANT_ID != null ? window.TOMAHAWK_VARIANT_ID : TOMAHAWK_VARIANT_ID);

    function ensureHost(){
      let el = document.getElementById('freebie-message');
      if (!el) {
        const host = document.querySelector('.Cart__recap, .cart__footer, #CartDrawer .drawer__inner, body');
        el = document.createElement('div'); el.id = 'freebie-message'; host?.prepend(el);
      }
      return el;
    }

    const codesOf = function(cart){
      const raw = (cart && Array.isArray(cart.discount_codes)) ? cart.discount_codes : [];
      return raw
        .filter(e => (typeof e === 'object' && 'applied' in e) ? !!e.applied : true)
        .map(e => (typeof e === 'object' ? String(e && e.code || '') : String(e || '')))
        .map(s => s.toUpperCase())
        .filter(Boolean);
    };

    function qtyById(cart, id){
      const items = (cart && cart.items) ? cart.items : [];
      return items.reduce((s, i) => s + (Number(i?.id) === Number(id) ? Number(i?.quantity || 0) : 0), 0);
    }

    const _fallback = window.updateFreebieBanner;

    window.updateFreebieBanner = async function(maybeCart){
      const el = ensureHost();
      if (!el) return _fallback?.(maybeCart);

      if (APPLYING_CODE) {
        el.innerHTML = `
          <span class="freebie-badge freebie-badge--load">
            <span class="freebie-spinner" aria-hidden="true"></span>
            <span>Applying code…</span>
          </span>`;
        return;
      }

      let cart = maybeCart;
      try { if (!cart) cart = await getCart(); } catch(_) {}
      if (!cart) return _fallback?.(maybeCart);

      const codes = new Set(codesOf(cart));
      const is66 = codes.has('FREEDOM66');
      const is99 = codes.has('FREEDOM99');
      if (!(is66 || is99)) return _fallback?.(cart);

      const { hasCode, meetsSpend } = isEligible(cart);
      const label     = is66 ? 'Ultralight Tactical Tomahawk' : 'Spy Blade XL (Freedom)';
      const variantId = is66 ? TOMAHAWK_ID : BLADE_ID;
      const present   = qtyById(cart, variantId) >= 1;

      if (hasCode && !meetsSpend) {
        const diff = ((THRESHOLD_CENTS) - (cart.items_subtotal_price || 0)) / 100;
        el.innerHTML = `
          <span class="freebie-badge freebie-badge--warn">
            <span class="freebie-dot" aria-hidden="true"></span>
            <span>Add $${diff.toFixed(2)} more to unlock <strong>1 ${label}</strong>.</span>
          </span>`;
        return;
      }

      if (!present) {
        el.innerHTML = `
          <span class="freebie-badge freebie-badge--load">
            <span class="freebie-spinner" aria-hidden="true"></span>
            <span>Adding 1 Free ${label}…</span>
          </span>`;
        return;
      }

      el.innerHTML = `
        <span class="freebie-badge freebie-badge--ok">
          <svg class="freebie-icon" viewBox="0 0 16 16" aria-hidden="true">
            <path fill="currentColor" d="M6.2 11.3 2.9 8l1.3-1.3 2 2 5-5L12.7 5l-6.5 6.3z"/>
          </svg>
          <span>1 Free ${label} added</span>
        </span>`;
    };

    document.addEventListener('DOMContentLoaded', () => { try { window.updateFreebieBanner?.(); } catch(_) {} });
  })();

})();
